import * as React from 'react'
import { Loader2 } from 'lucide-react'
import {
  createOntologyHeaders,
  getApiKey,
  getOntologyApiBaseUrl,
  isIdentityEnabled,
  setApiKey
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { StatusPill } from '../../../components/primitives/StatusPill'
import type { Status } from '../../../components/primitives/types'

type TestStatus = 'idle' | 'loading' | 'ok' | 'error'

const STATUS_PILL: Record<TestStatus, { status: Status; label: string }> = {
  idle: { status: 'idle', label: 'not tested' },
  loading: { status: 'info', label: 'testing…' },
  ok: { status: 'run', label: 'connected' },
  error: { status: 'down', label: 'failed' }
}

export function ConnectionCard() {
  const identityEnabled = isIdentityEnabled()

  if (identityEnabled) {
    return <IdentityConnectionCard />
  }

  return <ApiKeyConnectionCard />
}

function IdentityConnectionCard() {
  const endpoint = getOntologyApiBaseUrl()
  const [testStatus, setTestStatus] = React.useState<TestStatus>('idle')
  const [testError, setTestError] = React.useState('')

  const handleTest = async () => {
    setTestStatus('loading')
    setTestError('')
    try {
      const response = await fetch(`${endpoint}/health`, {
        headers: createOntologyHeaders()
      })
      if (response.ok) {
        setTestStatus('ok')
      } else if (response.status === 401) {
        setTestStatus('error')
        setTestError('Authentication failed — sign in again and retry.')
      } else {
        setTestStatus('error')
        setTestError(`Server returned ${response.status}`)
      }
    } catch {
      setTestStatus('error')
      setTestError('Could not reach proxy server.')
    }
  }

  const pill = STATUS_PILL[testStatus]

  return (
    <Card className="gap-3 py-4">
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            <h3 className="text-sm font-semibold text-ink">Proxy server connection</h3>
            <span className="font-mono text-11 text-ink-3">{endpoint}</span>
          </div>
          <StatusPill status={pill.status}>{pill.label}</StatusPill>
        </div>

        <p className="text-xs text-ink-3">
          Requests authenticate with your signed-in Axilon account. No API key is required in
          Settings.
        </p>

        {testError ? (
          <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
            {testError}
          </div>
        ) : null}

        <div className="flex flex-row items-center gap-2">
          <Button
            size="xs"
            variant="outline"
            disabled={testStatus === 'loading'}
            onClick={() => void handleTest()}
          >
            {testStatus === 'loading' ? <Loader2 className="animate-spin" /> : null}
            Test connection
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function ApiKeyConnectionCard() {
  const endpoint = getOntologyApiBaseUrl()
  const [apiKey, setApiKeyLocal] = React.useState(() => getApiKey())
  const [testStatus, setTestStatus] = React.useState<TestStatus>('idle')
  const [testError, setTestError] = React.useState('')
  const [saved, setSaved] = React.useState(false)

  const hasKey = apiKey.trim().length > 0
  const isDirty = apiKey !== getApiKey()

  const handleSave = () => {
    setApiKey(apiKey.trim())
    setSaved(true)
    window.setTimeout(() => setSaved(false), 2000)
  }

  const handleTest = async () => {
    setTestStatus('loading')
    setTestError('')
    const trimmedKey = apiKey.trim()
    try {
      const response = await fetch(`${endpoint}/health`, {
        headers: { 'X-API-Key': trimmedKey }
      })
      if (response.ok) {
        setTestStatus('ok')
        if (trimmedKey !== getApiKey()) {
          setApiKey(trimmedKey)
          setApiKeyLocal(trimmedKey)
          setSaved(true)
          window.setTimeout(() => setSaved(false), 2000)
        }
      } else if (response.status === 401) {
        setTestStatus('error')
        setTestError('Authentication failed — check your API key.')
      } else {
        setTestStatus('error')
        setTestError(`Server returned ${response.status}`)
      }
    } catch {
      setTestStatus('error')
      setTestError('Could not reach proxy server.')
    }
  }

  const pill = STATUS_PILL[testStatus]

  return (
    <Card className="gap-3 py-4">
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            <h3 className="text-sm font-semibold text-ink">Proxy server connection</h3>
            <span className="font-mono text-11 text-ink-3">{endpoint}</span>
          </div>
          <StatusPill status={pill.status}>{pill.label}</StatusPill>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="settings-api-key">HMAC API key</Label>
          <Input
            id="settings-api-key"
            type="password"
            placeholder="Paste your signed API key"
            value={apiKey}
            onChange={(event) => {
              setApiKeyLocal(event.target.value)
              setSaved(false)
              setTestStatus('idle')
            }}
            className="font-mono"
          />
          <p className="text-xs text-ink-3">
            Sent as the <code className="font-mono text-11">X-API-Key</code> header on every
            request. Same format as the Axilon AI desktop app.
          </p>
        </div>

        {testError ? (
          <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
            {testError}
          </div>
        ) : null}

        {!hasKey && getApiKey() === '' ? (
          <div role="status" className="rounded-md bg-st-warn-bg px-3 py-2 text-xs text-st-warn">
            No API key configured. Requests to the proxy server will fail with 401.
          </div>
        ) : null}

        <div className="flex flex-row items-center gap-2">
          <Button
            size="xs"
            variant="outline"
            disabled={!hasKey || testStatus === 'loading'}
            onClick={() => void handleTest()}
          >
            {testStatus === 'loading' ? <Loader2 className="animate-spin" /> : null}
            Test connection
          </Button>
          <Button size="xs" variant="outline" disabled={!isDirty && hasKey} onClick={handleSave}>
            Save
          </Button>
          {saved ? (
            <span className="rounded-sm bg-st-run-bg px-1.5 py-0.5 text-10 font-medium text-st-run">
              SAVED
            </span>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}
